import { useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import { Button, Group, Loader, PasswordInput, Stack, Text, TextInput } from '@mantine/core'
import { useQueryClient } from '@tanstack/react-query'

import {
  CHAVE_REDE_PADRAO, definirRedePadrao, removerRedePadrao, useRedePadrao, type RedePadrao as Rede,
} from '@/api/robo'
import { Callout, DataCard } from '@/components/ui'
import { bytesUtf8 } from '@/features/robo-wifi/redes'
import { RECUSA_WIFI } from './WifiDoRobo'

/**
 * O que acontece depois de definir, dito na tela onde se decide (spec
 * 2026-09-29-robo-wifi-padrao, 4.2). A segunda frase é a consequência aceita no §2.
 */
export const O_QUE_ACONTECE =
  'Todo robô ativo que ainda não tem esta rede a recebe em até 5 minutos, assim que falar com o '
  + 'Console. Quem tiver a placa na mão consegue ler a senha.'

function frase(erro: unknown): string {
  const detail = isAxiosError(erro) ? erro.response?.data?.detail : undefined
  return (typeof detail === 'string' && RECUSA_WIFI[detail]) || 'Não foi possível falar com o console.'
}

const data = (epoch: number) => new Date(epoch * 1000).toLocaleDateString('pt-BR')

/**
 * A rede Wi-Fi padrão dos robôs, na Frota. Quem monta este bloco já conferiu
 * `robo:manage` global, a mesma pergunta que o backend faz.
 *
 * A senha nunca entra em estado do React nem no cache: o campo é não
 * controlado, lido no envio e apagado na mesma hora (como `useCampoDeSenha` do
 * `WifiDoRobo`). O console também nunca a devolve.
 */
export function RedePadrao({ nomeDe }: { nomeDe: (id: number | null) => string | null }) {
  const qc = useQueryClient()
  const consulta = useRedePadrao(true)
  const [editando, setEditando] = useState(false)
  const [ssid, setSsid] = useState('')
  const senha = useRef<HTMLInputElement>(null)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [removendo, setRemovendo] = useState(false)
  // Senha vazia = rede aberta para a frota inteira, e quem só corrigiu o nome
  // chega aqui sem querer: pede a confirmação antes de mandar.
  const [confirmarAberta, setConfirmarAberta] = useState(false)

  const rede: Rede | null = consulta.data ?? null
  const formulario = !rede || editando
  const bytesSsid = bytesUtf8(ssid)
  const ssidRuim = ssid !== '' && (bytesSsid < 1 || bytesSsid > 32)

  const tirarSenha = (): string => {
    const valor = senha.current?.value ?? ''
    if (senha.current) senha.current.value = ''
    return valor
  }

  const salvar = () => {
    const valor = tirarSenha()
    if (bytesUtf8(valor) > 63) {
      setErro(RECUSA_WIFI.senha_invalida)
      return
    }
    if (valor === '') {
      setErro(null)
      setConfirmarAberta(true)
      return
    }
    void enviar(valor)
  }

  const enviar = async (valor: string) => {
    setConfirmarAberta(false)
    setEnviando(true)
    setErro(null)
    try {
      const nova = await definirRedePadrao(ssid, valor)
      qc.setQueryData(CHAVE_REDE_PADRAO, nova)
      setEditando(false)
    } catch (e) {
      setErro(frase(e))
    } finally {
      setEnviando(false)
    }
  }

  const remover = async () => {
    setEnviando(true)
    setErro(null)
    try {
      await removerRedePadrao()
      qc.setQueryData(CHAVE_REDE_PADRAO, null)
      setRemovendo(false)
    } catch (e) {
      setErro(frase(e))
    } finally {
      setEnviando(false)
    }
  }

  const abrirTroca = () => {
    setSsid(rede?.ssid ?? '')
    setEditando(true)
    setErro(null)
  }

  let corpo
  if (consulta.isLoading) {
    corpo = <Loader size="sm" />
  } else if (consulta.isError) {
    corpo = <Text size="sm" c="red.7">Não foi possível consultar a rede padrão.</Text>
  } else if (formulario) {
    corpo = (
      <Stack gap="xs">
        <Group gap="sm" align="flex-end" wrap="wrap">
          <TextInput label="Nome da rede" value={ssid} w={260}
            onChange={(ev) => setSsid(ev.currentTarget.value)}
            error={ssidRuim ? RECUSA_WIFI.ssid_invalido : undefined} />
          {/* Não controlado: o valor só existe no campo até o envio. */}
          <PasswordInput label="Senha" ref={senha} w={260} autoComplete="new-password"
            description="Vazia para rede aberta." />
          <Button onClick={salvar} loading={enviando} disabled={ssid === '' || ssidRuim}>
            {rede ? 'Salvar' : 'Definir'}
          </Button>
          {rede && (
            <Button variant="default" disabled={enviando}
              onClick={() => { tirarSenha(); setEditando(false); setErro(null); setConfirmarAberta(false) }}>
              Cancelar
            </Button>
          )}
        </Group>
        {confirmarAberta && (
          <Group gap="sm" wrap="wrap">
            <Text size="sm">
              {`Sem senha, todo robô ativo recebe ${ssid} como rede aberta, e quem já tem a senha certa a perde.`}
            </Text>
            <Button size="xs" color="red" loading={enviando} onClick={() => void enviar('')}>
              Definir como rede aberta
            </Button>
            <Button size="xs" variant="default" disabled={enviando} onClick={() => setConfirmarAberta(false)}>
              Voltar
            </Button>
          </Group>
        )}
      </Stack>
    )
  } else if (rede) {
    const quem = nomeDe(rede.definida_por)
    corpo = (
      <Stack gap="xs">
        <Group gap="sm" wrap="wrap">
          <Text size="sm">
            <Text span fw={600}>{rede.ssid}</Text>
            {`, definida${quem ? ` por ${quem}` : ''} em ${data(rede.definida_em)}`}
            {!rede.com_senha && ' (rede aberta)'}
          </Text>
          <Button variant="default" size="xs" disabled={enviando} onClick={abrirTroca}>
            Trocar rede ou senha
          </Button>
          {!removendo && (
            <Button variant="light" size="xs" color="red" disabled={enviando}
              onClick={() => setRemovendo(true)}>
              Remover
            </Button>
          )}
        </Group>
        {removendo && (
          <Group gap="sm" wrap="wrap">
            <Text size="sm">
              Os robôs mantêm a rede já gravada; só param as entregas novas.
            </Text>
            <Button size="xs" color="red" loading={enviando} onClick={remover}>Remover</Button>
            <Button size="xs" variant="default" disabled={enviando} onClick={() => setRemovendo(false)}>
              Manter
            </Button>
          </Group>
        )}
      </Stack>
    )
  }

  return (
    <DataCard title="Rede padrão dos robôs">
      <Stack gap="sm">
        <Text size="xs" c="dimmed" style={{ maxWidth: 860 }}>{O_QUE_ACONTECE}</Text>
        {corpo}
        {erro && <Callout variant="warning">{erro}</Callout>}
      </Stack>
    </DataCard>
  )
}
